import React, { useMemo, useState } from "react";
import TradeCard from "features/dashboard/OpenTrades/TradeCard";
import { NewLookDashboardTradeCard } from "features/dashboard/OpenTrades/NewLookTradeCard.tsx";
import { SortingOrder } from "types";
import {
  MOBILE_TRADE_CARD_HEIGHT,
  MOBILE_TRADE_CARD_WIDTH,
  TRADE_CARD_HEIGHT,
  TRADE_CARD_WIDTH,
} from "features/dashboard/common/constants";
import VirtualCardsList from "common/Lists/VirtualCardsList";
import { EmptyWrapper, SectionTitle } from "features/dashboard/common/styles";
import { OPEN_TRADES_PATH, TABLET } from "common/constants";
import Title from "common/Typography/Title";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import styled, { useTheme } from "styled-components";
import { ChevronRight, Flame } from "lucide-react";
import RouterLink from "common/Buttons/RouterLink";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { Pill, SectionHeader, Surface } from "common/NewLook";
import Flex from "common/Boxes/Flex";
import SortSelect from "common/Select/SortSelect";
import useMedia from "utils/hooks/useMedia";
import useMeasure from "utils/hooks/useMeasure";
import { DASHBOARD_TRADE_GRID_ID } from "testIds";
import Panel from "common/NewLook/Panel.tsx";
import OpenTradesList from "features/dashboard/instrument/OpenTradesList.tsx";
import { NEW_LOOK_RADIUS, getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { getSortedDataBySortSelect } from "@shared/utils/general";
import { VIRTUAL_CARDS_LIST_PADDING } from "@shared/common/constants";
import { hexToRGB } from "@shared/utils/misc";
import {
  GetTradesRequest,
  LanguageNameSpaces,
  SocketTradeData,
} from "@shared/types";
import useGetVirtualListCardDimensions from "utils/hooks/useGetVirtualListCardDimensions";
import TradesEmpty from "features/trades/TradesEmpty";
import PhoneTradesList from "features/dashboard/PhoneDashboard/PhoneTradesList";
import {
  PhoneCard,
  PhoneSectionHead,
} from "features/dashboard/PhoneDashboard/PhoneParts";
import { HOT_TRADES_HELPER_TOOLTIP } from "@shared/common/Tooltips/constants";
import { useGetTradesQueries } from "@shared/features/trades/common/hooks";
import { skipToken } from "@reduxjs/toolkit/query";
import { useBottomNavigator, useOnReconnect } from "utils/hooks/misc";
import { isArray } from "lodash";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface TradeCardsProps {
  getTradesArgs: GetTradesRequest | undefined;
}

const TradeCards = ({ getTradesArgs }: TradeCardsProps) => {
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const { isNewLook } = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const [measureTrades, tradesSize] = useMeasure<HTMLElement>();
  const tradeColumns = Math.max(
    1,
    Math.floor(
      (tradesSize.width + NEW_LOOK_CARD_GAP) /
        (NEW_LOOK_CARD_MIN_WIDTH + NEW_LOOK_CARD_GAP)
    )
  );
  const isPhone = useMedia(["(max-width: 600px)"], [true], false);
  const visibleTradeCount = isPhone
    ? 2
    : Math.min(NEW_LOOK_MAX_TRADES, tradeColumns * 2);
  const { getOpenTradesStatus, socketTradesData } = useGetTradesQueries({
    openTradesArgs: getTradesArgs,
    closedTradesArgs: skipToken,
    getOpen: true,
    getClosed: false,
    delayUpdateOnClose: 3000,
    useOnReconnect,
  });
  const {
    data,
    isLoading: getOpenTradesLoading,
    isUninitialized: getOpenTradesUninitialized,
  } = getOpenTradesStatus;
  const openTradesData: SocketTradeData[] | undefined = data?.data;

  const isTablet = useMedia([`(max-width: ${TABLET})`], [true], false);
  const { cardHeight, cardWidth } = useGetVirtualListCardDimensions(
    TRADE_CARD_HEIGHT,
    MOBILE_TRADE_CARD_HEIGHT,
    TRADE_CARD_WIDTH,
    MOBILE_TRADE_CARD_WIDTH
  );

  const [sortBy, setSortBy] = useState<TradeCardsSort>(
    TradeCardsSort.NEXT_TAKE_PROFIT
  );
  const [sortDirection, setSortDirection] = useState<SortingOrder>(
    SortingOrder.ASCEND
  );

  const sortedTrades = useMemo(() => {
    return getSortedDataBySortSelect<SocketTradeData, TradeCardsSort>(
      openTradesData || [],
      sortBy,
      sortDirection,
      SORT_TO_PATH,
      STRING_SORTS,
      ABS_SORTS
    ).map((trade) => {
      let socketTradeData;
      if (isArray(socketTradesData)) {
        socketTradeData = socketTradesData?.find(
          (socketTrade) => socketTrade.id === trade.id
        );
      }
      if (socketTradeData)
        return { ...trade, status_value: socketTradeData.status_value };
      return trade;
    });
  }, [openTradesData, sortBy, sortDirection, socketTradesData]);

  const getNumOfHotTrades = () => {
    let numOfHotTrades = 0;
    openTradesData?.map((trade) => {
      if (trade.closest_order?.is_hot) numOfHotTrades += 1;
    });
    return numOfHotTrades;
  };

  const hotTradeTagContent = t("hot_trades.tag", {
    numHotTrades: getNumOfHotTrades(),
    numTotalTrades: openTradesData?.length || 0,
  });

  const title = (
    <TitleWrapper>
      <Flex $justifyContent="start">
        <SectionTitle>{t("open_trades.title")}</SectionTitle>
        <HotTradesTag $isMobile={isTablet}>{hotTradeTagContent}</HotTradesTag>
        <Title level={3} type="secondary">
          <HelperTooltip
            {...HOT_TRADES_HELPER_TOOLTIP}
            description={tCommon(HOT_TRADES_HELPER_TOOLTIP.description)}
            title={tCommon(HOT_TRADES_HELPER_TOOLTIP.title)}
          />
        </Title>
      </Flex>
      <SortSelect
        setSortBy={setSortBy}
        sortBy={sortBy}
        sortDirection={sortDirection}
        setSortDirection={setSortDirection}
        sortValues={Object.values(TradeCardsSort)}
        labelMapper={(value) => t(value)}
      />
    </TitleWrapper>
  );

  const noTrades =
    !getOpenTradesUninitialized && openTradesData && !openTradesData.length;

  const sortSelect = (
    <SortSelect
      setSortBy={setSortBy}
      sortBy={sortBy}
      sortDirection={sortDirection}
      setSortDirection={setSortDirection}
      sortValues={Object.values(TradeCardsSort)}
      labelMapper={(value) => t(value)}
    />
  );

  if (isNewLook && !isBottomNavigator) {
    const numHotTrades = getNumOfHotTrades();
    return (
      <Panel
        title={t("open_trades.title")}
        count={openTradesData?.length || undefined}
        extra={
          !noTrades &&
          numHotTrades > 0 && (
            <HotNote>
              <Flame size={12} strokeWidth={2.25} />
              {hotTradeTagContent}
              <HelperTooltip
                {...HOT_TRADES_HELPER_TOOLTIP}
                description={tCommon(HOT_TRADES_HELPER_TOOLTIP.description)}
                title={tCommon(HOT_TRADES_HELPER_TOOLTIP.title)}
              />
            </HotNote>
          )
        }
        actions={!noTrades && <QuietSort>{sortSelect}</QuietSort>}
        seeAllTo={OPEN_TRADES_PATH}
        seeAllLabel={tCommon("labels.see_all")}
        flush={true}
        data-testid="dashboard-open-trades"
      >
        {noTrades ? (
          <NewLookEmpty>
            <TradesEmpty isOpenTrades={true} iconWidth={88} />
          </NewLookEmpty>
        ) : (
          <OpenTradesList
            trades={sortedTrades}
            allTrades={openTradesData}
            loading={getOpenTradesLoading}
            maxRows={DESKTOP_MAX_ROWS}
          />
        )}
      </Panel>
    );
  }

  if (isNewLook && isBottomNavigator) {
    return (
      <section>
        <PhoneSectionHead
          title={t("open_trades.title")}
          seeAllTo={OPEN_TRADES_PATH}
          seeAllLabel={tCommon("labels.see_all")}
        />
        {noTrades ? (
          <PhoneCard>
            <TradesEmpty isOpenTrades={true} iconWidth={88} />
          </PhoneCard>
        ) : (
          <PhoneTradesList
            trades={sortedTrades}
            allTrades={openTradesData}
            loading={getOpenTradesLoading}
          />
        )}
      </section>
    );
  }

  if (isNewLook) {
    const renderContent = () => {
      if (noTrades)
        return (
          <Surface $padding="24px 20px">
            <TradesEmpty isOpenTrades={true} iconWidth={88} />
          </Surface>
        );
      if (getOpenTradesLoading)
        return (
          <LoadingSpinnerOverlay isLoading={true} spinnerMarginTop={96}>
            <div style={{ height: NEW_LOOK_CARD_HEIGHT }} />
          </LoadingSpinnerOverlay>
        );
      return (
        <NewLookGrid data-testid={DASHBOARD_TRADE_GRID_ID}>
          {sortedTrades.slice(0, visibleTradeCount).map((trade) => (
            <NewLookDashboardTradeCard key={trade.id} tradeData={trade} />
          ))}
        </NewLookGrid>
      );
    };
    return (
      <section ref={measureTrades} style={{ minWidth: 0 }}>
        <SectionHeader
          title={t("open_trades.title")}
          count={isPhone ? openTradesData?.length : undefined}
          extra={
            !noTrades &&
            !isPhone && (
              <>
                <Pill tone="blue" icon={Flame}>
                  {hotTradeTagContent}
                </Pill>
                <HelperTooltip
                  {...HOT_TRADES_HELPER_TOOLTIP}
                  description={tCommon(HOT_TRADES_HELPER_TOOLTIP.description)}
                  title={tCommon(HOT_TRADES_HELPER_TOOLTIP.title)}
                />
              </>
            )
          }
          actions={
            <>
              {!noTrades && !isPhone && sortSelect}
              <RouterLink to={OPEN_TRADES_PATH}>
                <NewLookSeeAll>
                  {tCommon("labels.see_all")}
                  <ChevronRight size={14} strokeWidth={2} />
                </NewLookSeeAll>
              </RouterLink>
            </>
          }
        />
        {renderContent()}
      </section>
    );
  }

  const empty = noTrades && (
    <>
      <div
        style={{
          margin: `0 ${VIRTUAL_CARDS_LIST_PADDING}px 8px ${VIRTUAL_CARDS_LIST_PADDING}px`,
        }}
      >
        {title}
      </div>
      <EmptyWrapper $cardHeight={cardHeight} style={{ paddingTop: 24 }}>
        <TradesEmpty isOpenTrades={true} iconWidth={100} />
      </EmptyWrapper>
    </>
  );

  return (
    <Wrapper>
      {empty || (
        <VirtualCardsList
          cardsData={sortedTrades}
          loading={getOpenTradesLoading}
          cardHeight={cardHeight}
          cardWidth={cardWidth}
          CardComponent={TradeCardComponent}
          seeAllLink={OPEN_TRADES_PATH}
          title={title}
        />
      )}
    </Wrapper>
  );
};

export default React.memo(TradeCards);

interface TradeCardComponentProps {
  data: SocketTradeData;
  extraData?: unknown;
  index: number;
}

const TradeCardComponent = ({ data }: TradeCardComponentProps) => {
  return <TradeCard tradeData={data} />;
};

export enum TradeCardsSort {
  NEXT_ENTRY_OR_STOP = "open_trades.sort.next_entry_or_stop_distance",
  NEXT_TAKE_PROFIT = "open_trades.sort.next_take_profit_distance",
  SYMBOL = "open_trades.sort.symbol",
  AMOUNT = "open_trades.sort.amount",
  PROFIT_LOSS = "open_trades.sort.profit_loss",
}
const SORT_TO_PATH: { [sortKey in TradeCardsSort]?: string | string[] } = {
  [TradeCardsSort.NEXT_TAKE_PROFIT]: "next_take_profit.distance",
  [TradeCardsSort.NEXT_ENTRY_OR_STOP]: [
    "next_entry.distance",
    "next_stop.distance",
  ],
  [TradeCardsSort.SYMBOL]: "symbol",
  [TradeCardsSort.AMOUNT]: "allocated_amount_usd",
  [TradeCardsSort.PROFIT_LOSS]: "usd_amount_change",
};
const ABS_SORTS = [
  TradeCardsSort.NEXT_TAKE_PROFIT,
  TradeCardsSort.NEXT_ENTRY_OR_STOP,
];
const STRING_SORTS = [TradeCardsSort.SYMBOL];

const Wrapper = styled.div`
  min-height: 240px;
`;
const NEW_LOOK_MAX_TRADES = 8;
/** Desktop dashboard exploration: rows shown in the open trades panel. */
const DESKTOP_MAX_ROWS = 6;
// A quiet note, not a tinted pill: the count is information, not a call to act.
const HotNote = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
  svg:first-child {
    color: ${(p) => getNewLookTokens(p.theme).stop};
  }
`;
// In the panel header the sort control drops its outline until hovered, so the
// title, not the control, leads the header.
const QuietSort = styled.div`
  display: inline-flex;
  & > div {
    height: 30px;
    border-color: transparent;
    background: transparent;
    transition: border-color 120ms;
  }
  &:hover > div,
  &:focus-within > div {
    border-color: ${(p) => getNewLookTokens(p.theme).hairlineStrong};
  }
`;
const NewLookEmpty = styled.div`
  padding: 16px 20px 24px;
`;

const NEW_LOOK_CARD_MIN_WIDTH = 300;
const NEW_LOOK_CARD_GAP = 16;
const NEW_LOOK_CARD_HEIGHT = 232;
const NewLookGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(min(100%, ${NEW_LOOK_CARD_MIN_WIDTH}px), 1fr)
  );
  gap: ${NEW_LOOK_CARD_GAP}px;
  @media (max-width: 600px) {
    gap: 8px;
  }
`;
const NewLookSeeAll = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 13px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  white-space: nowrap;
`;
const HotTradesTag = styled.div<{ $isMobile: boolean }>`
  color: ${(p) => p.theme.colors.primaryBlue};
  background: ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.1)};
  border-radius: 6px;
  padding: 4px 8px;
  margin-left: 8px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    border-radius: ${NEW_LOOK_RADIUS.chip}px;
    padding: 4px 12px;
  `}
  font-size: ${(p) =>
    p.$isMobile
      ? p.theme.typography.fontSizes.S
      : p.theme.typography.fontSizes.M};
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
`;
const TitleWrapper = styled(Flex)`
  column-gap: 12px;
  row-gap: 12px;
  flex-wrap: wrap;
  flex: 1;
`;
